import { filter, take, BehaviorSubject } from 'rxjs';
import { systemError } from '../system-utils/events-manager';

type ConfigEntry = any;

class Config {
  private _config: Record<string, ConfigEntry>;
  private _ready: BehaviorSubject<boolean>;

  constructor() {
    this._config = null;
    this._ready = new BehaviorSubject(false);
  }

  /**
   * Calls a callback when the config has been parsed
   * @param callback
   */
  onReady(callback: () => void) {
    this._ready
      .pipe(
        filter(ready => ready),
        take(1),
      )
      .subscribe(callback);
  }

  /**
   * Parses a raw json config
   * @param raw
   * @returns whether the config was successfully parsed or not
   */
  parse(raw: string): boolean {
    try {
      this._config = JSON.parse(raw).config;
      if (!this._config) {
        throw new Error();
      }
    } catch {
      this._config = null;
      this._ready.next(false);
      systemError.publishCriticalError({ error: 'Failed to parse config' });
      return false;
    }
    this._ready.next(true);
    return true;
  }

  get<T extends ConfigEntry>(key: string, fallback?: T): T {
    if (!this._ready.value) {
      return fallback;
    }
    const nested = key.split('.');
    let value: ConfigEntry = this._config;
    const found = nested.every(nestedKey => {
      if (nestedKey in value) {
        value = value[nestedKey];
        return true;
      }
      return false;
    });
    return found ? value : fallback;
  }
}

const config = new Config();
export default config;
